import { gql, useMutation } from '@apollo/client';
import { useState } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./components/ResultTable";
import { Pda } from "./components/Pda";
import { parseException, showError } from "utils/exceptionUtils";
import { ScanInput } from "./components/ScanInput";
import { Header } from './components/Header';
import { isBagNumber, showBarCodeError } from '../../utils/BarCodeUtil';
import { playScanFailedAoSound } from "../../utils/scanUtils";
import { t } from '../../i18n';
import { useScanQueueConsumer } from "./components/useScanQueueConsumer";


export interface HandleScanEventResult {
  success: boolean;
}

const HANDLE_WHS_UNBAGGING_SCAN_EVENT = gql`
mutation HandleWhsUnbaggingScanEvent($bagNumber: String!) {
    success: handleWhsUnbaggingScanEvent(bagNumber: $bagNumber)
}
`

const PageBody = () => {

  const [handleWhsUnbaggingScanEvent] = useMutation<HandleScanEventResult>(HANDLE_WHS_UNBAGGING_SCAN_EVENT);

  const handleScanEvent = async (bagNumber: string): Promise<HandleScanEventResult> => {
    const { data } = await handleWhsUnbaggingScanEvent({ variables: { bagNumber } });
    return { success: data?.success || false };
  };

  const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
  const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
  const [bagNumber, setBagNumber] = useState('');

  const onProcess = async (event: { id: string; seq: number; code: string; ts: number }) => {
    const barCode = event.code;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const value = barCode.trim();
    if (value === '') {
      playScanFailedAoSound();
      showError({ title: t('pda.common.error'), message: t('pda.common.bagCodeRequired') });
      return;
    }

    setBagNumber(value);

    try {
      const { success } = await handleScanEvent(value);
      if (success) {
        setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({ value1: value, seq: event.seq })));
        setBagNumber('');
      }
    } catch (error) {
      const { title, message: errorMessage } = parseException(error);
      playScanFailedAoSound();
      showError({ title, message: errorMessage });
      setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: value, failedReason: errorMessage, seq: event.seq })));
      setBagNumber('');
    }
  };

  const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const handleScan = (code: string) => {
    if (isBagNumber(code)) {
      enqueueScan(code);
    } else {
      showBarCodeError(code);
    }
  };

  const successColumns: Column[] = [
    { title: t('pda.pages.unbagging.bagCode'), key: 'value1' },
    { title: t('pda.common.time'), key: 'time' }
  ];

  const failedColumns = [
    ...successColumns,
    { title: t('pda.common.failedReason'), key: 'failedReason' }
  ];

  return (
    <div className="flex flex-col gap-2">
      <ScanInput placeholder={t('pda.pages.unbagging.bagCode')} value={bagNumber} onScan={handleScan} />

      <ResultTable
        successRecords={successRecords}
        failedRecords={failedRecords}
        successColumns={successColumns}
        failedColumns={failedColumns}
      />
    </div>
  );

}

export default function UnbaggingPage() {
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2">
      <Header title={t('pda.pages.unbagging.unbaggingScan')} />
      <Pda />
      <PageBody />
    </div>
  );
}
